import * as Constants from '../../shared/Constants';

import {
  EmitterSubscription,
  NativeEventEmitter,
  NativeModules,
  Platform,
  View,
} from 'react-native';
import { KitLoader, KitSnack } from '@omni/kit/components';
import {
  NavigationProp,
  ParamListBase,
  useIsFocused,
  useNavigation,
} from '@react-navigation/native';
import React, { useEffect } from 'react';
import { SizeClass, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import {
  initState,
  logout,
  resolveChatUserThunk,
  setViewReady,
} from '../../shared/redux/actions/SystemActions';
import {
  isNewOmniVersion,
  removePendingCommandFromDevice,
  retrieveIntroPresentedFromDevice,
  retrievePendingCommandFromDevice,
  savePendingCommandToDevice,
} from '../../utilities/mobileStorage';
import {
  setChannel,
  setInviteAccepted,
} from '../../shared/redux/actions/ChatActions';
import {
  systemErrorSelector,
  userIdSelector,
  viewIsReadySelector,
} from '../../shared/redux/selectors';
import { useDispatch, useSelector } from 'react-redux';

import ChatService from '../../services/ChatService';
import ErrorView from './ErrorView';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import HealthMonitor from '@omni/kit/HealthMonitor';
import { SendbirdMessage } from '../Types';
import { sbGetGroupChannel } from '../../utilities/sendbird/channelFunctions';
import { sbGetMessage } from '../../utilities/sendbird/chatFunctions';
import { sbIsConnected } from '../../utilities/sendbird/userFunctions';
import { BaseMessage } from '@sendbird/chat/message';

const { ReactPlatformBridge } = NativeModules;

const debug = require('debug')('omni:chat:AppLoadingView');

const whatsNewData = require('../../../blocks/templates/examples/whatsNew.json');

export type ModuleCommand = {
  chat_client_channel_id?: string;
  name?: string;
  require_login: boolean | string;
  resource_uuid?: string;
  sendbird?: string;
  value?: string;
};

type Props = {
  screenProps: {
    appKey?: string;
    brandColor?: string;
    moduleCommand?: ModuleCommand;
  };
  navToChatScreen?: () => void;
  showIntroModal?: () => void;
};

export default function AppLoadingView({
  screenProps,
  navToChatScreen = undefined,
  showIntroModal = undefined,
}: Props): JSX.Element {
  let navigation: NavigationProp<ParamListBase>,
    isFocused = true;

  /**
   * FIXME: Can we unwrap these? Hooks should never be conditional
   * https://typeofnan.dev/fix-the-react-hook-is-called-conditionally-error-in-react/
   */
  try {
    navigation = useNavigation();
    isFocused = useIsFocused();
  } catch {}

  const dispatch = useDispatch();
  const isLargeWeb =
    useSizeClass().sizeClass !== SizeClass.Small && Platform.OS === 'web';

  const viewIsReady = useSelector(viewIsReadySelector);
  const userId = useSelector(userIdSelector);
  const systemError = useSelector(systemErrorSelector);
  const isError = Boolean(systemError);
  const sbConnected = sbIsConnected();

  const commandListeners: EmitterSubscription[] = [];

  //****************************************************************************
  // Methods
  //****************************************************************************

  const setViewIsReady = (value: boolean) => {
    dispatch(setViewReady(value));
  };

  const _navigateToChatScreen = navToChatScreen
    ? navToChatScreen
    : (channel: GroupChannel, message?: BaseMessage) => {
        debug('nav to chat screen ', channel);

        dispatch(setChannel(channel));
        setViewIsReady(true);

        navigation.navigate(Constants.CHAT_SCREEN, {
          channel,
          channelName: channel.name,
        });

        if (message?.parentMessageId) {
          navigation.navigate(Constants.CHAT_THREAD_SCREEN, {
            parentMessageId: message.parentMessageId,
            channelId: channel.url,
          });
        }
      };

  const _registerEmitters = () => {
    if (ReactPlatformBridge == null) return;

    const ReactPlatformBridgeEventEmitter = new NativeEventEmitter(
      ReactPlatformBridge
    );
    const commandSubscription = ReactPlatformBridgeEventEmitter.addListener(
      'ModuleCommand',
      (event) => {
        _dispatchCommand(event.moduleCommand);
      }
    );
    commandListeners.push(commandSubscription);
  };

  const _resolvePendingCommand = async () => {
    try {
      const pendingCommand = await retrievePendingCommandFromDevice();
      debug('Retrieve @pendingCommand from AsyncStorage: ', pendingCommand);

      if (pendingCommand !== null) return pendingCommand;
    } catch (e) {
      debug('Error reading pending commands');
    }

    return null;
  };

  const _dispatchCommand = async (
    commandObject?: ModuleCommand
  ): Promise<boolean> => {
    // Return true if we handle navigation with the moduleCommand
    if (!commandObject) return false;

    if (
      (commandObject.require_login === true ||
        commandObject.require_login === 'true') &&
      !userId
    ) {
      /**
       * TODO: Investigate whether we can remove pendingCommand behavior to
       * simplify Messaging.  Will need to test opening Messaging push
       * notifications, accepting invites and logging in without pending
       * commands.
       */
      try {
        debug('Save pending command to device: ', commandObject);
        savePendingCommandToDevice(commandObject);
      } catch (e) {
        debug('Error storing pending command');
      }

      /**
       * A returning user may have opened a push notification for Messaging.
       * We need to resolve the chat user here to avoid an infinite loading
       * state.
       */
      await dispatch(resolveChatUserThunk());

      return false;
    }

    // Reset pending command object in storage and attempt to execute
    try {
      debug('Remove pending command from device...');
      await removePendingCommandFromDevice();
    } catch (e) {
      debug('Error resetting pending command');
    }

    debug(
      'Dispatching command',
      JSON.stringify(commandObject),
      commandObject.name
    );

    // Wait for connection with Sendbird
    if (!moduleCommandSuppressConnection(commandObject)) {
      await dispatch(resolveChatUserThunk());
    }

    // User invite
    if (commandObject.name === Constants.COMMAND_INVITE_USER) {
      debug('Accepting invite');
      ChatService.patch(
        'channel-invite',
        commandObject.resource_uuid as string,
        { accepted: true }
      )
        .then((invite: any) =>
          sbGetGroupChannel(invite.data.chat_client_channel_id)
        )
        .then((channel) => {
          KitSnack.show('Successfully joined channel');
          dispatch(setInviteAccepted(true));
          _navigateToChatScreen(channel);
        })
        .catch((error) => {
          HealthMonitor.logError(error);
          KitSnack.show(
            error.status === 422
              ? error.message
              : 'Error accepting channel invite.'
          );

          setViewIsReady(true);
        });

      return true;
    }

    if (commandObject.name === Constants.COMMAND_HANDLE_NOTIFICATION) {
      const sendbirdPayload = JSON.parse(commandObject.sendbird as string);
      let loadedChannel: GroupChannel;
      sbGetGroupChannel(sendbirdPayload.channel.channel_url)
        .then((channel: GroupChannel) => {
          loadedChannel = channel;

          return sendbirdPayload.message_id
            ? sbGetMessage(sendbirdPayload.message_id, channel)
            : undefined;
        })
        .then((message) => _navigateToChatScreen(loadedChannel, message));

      return true;
    }

    if (commandObject.name === Constants.COMMAND_LOGOUT) {
      debug('command object is logout: ', commandObject);
      dispatch(logout(true /*silent*/));

      return true;
    }

    if (commandObject.name === Constants.COMMAND_NAVIGATE_GROUP_CHANNEL) {
      sbGetGroupChannel(commandObject.chat_client_channel_id as string)
        .then((channel) => {
          if (channel.myMemberState === 'none') {
            KitSnack.show('Error joining channel', KitSnack.duration.LONG);
            // this.navToChannelListScreen();
            setViewIsReady(true);
          } else if (channel.myMemberState === 'joined') {
            _navigateToChatScreen(channel);
          } else {
            setViewIsReady(true);
          }
        })
        .catch((_e) => {
          setViewIsReady(true);
        });

      return true;
    }

    if (commandObject.name === Constants.COMMAND_NAVIGATE_INTRO) {
      if (showIntroModal) {
        showIntroModal();
      } else {
        navigation.navigate(Constants.INTRO_SCREEN);
      }

      return true;
    }

    if (commandObject.name === Constants.COMMAND_INVITE_CHANNEL_ADMIN) {
      ChatService.patch(
        'channel-admin-invite',
        commandObject.resource_uuid as string,
        { accepted: true }
      )
        .then(() => dispatch(resolveChatUserThunk))
        .catch(() => setViewIsReady(true));

      return true;
    }

    if (commandObject.name === Constants.COMMAND_REFRESH_CHAT_SESSION) {
      dispatch(resolveChatUserThunk);

      return true;
    }

    return false;
  };

  const moduleCommandSuppressConnection = (moduleCommand: {
    name?: string;
  }) => {
    switch (moduleCommand?.name) {
      /**
       * COMMAND_LOGOUT is a special case where we do not want to
       * inadvertently create a new chat user when doing a logout operation.
       * so we must suppress sbConnect and resolveChatUserThunk.
       * sbConnect will be called after this module command is dispatched
       */
      case Constants.COMMAND_LOGOUT:
        return true;

      default:
        return false;
    }
  };

  const _onRetryButtonPress = () => {
    NativeModules.ReactPlatformBridge.promptLogin();
  };

  //****************************************************************************
  // Lifecycle
  //****************************************************************************

  useEffect(() => {
    dispatchChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatchChat = async () => {
    if (!isLargeWeb) dispatch(initState(screenProps));

    const moduleCommand = screenProps?.moduleCommand;

    if (moduleCommand) {
      _dispatchCommand(moduleCommand);
    } else {
      await dispatch(resolveChatUserThunk());

      presentIntroModalIfNeeded();
    }
  };

  const presentIntroModalIfNeeded = () => {
    isNewOmniVersion().then((isNewVersion) => {
      retrieveIntroPresentedFromDevice().then((presentedIntro) => {
        if (
          isNewVersion &&
          (!presentedIntro || (whatsNewData?.blocks?.length ?? 0) > 0)
        ) {
          if (showIntroModal) {
            showIntroModal();
          } else {
            navigation.navigate(Constants.INTRO_SCREEN);
          }
        }
      });
    });
  };

  useEffect(() => {
    _registerEmitters();
    debug({ userId, viewIsReady });

    if (sbConnected && userId && !viewIsReady) {
      debug('Get pending command');
      _resolvePendingCommand().then((pendingCommand) => {
        if (pendingCommand) {
          debug('Dispatching pending command...');
          _dispatchCommand(pendingCommand);
        } else if (Platform.OS === 'web') {
          setViewIsReady(true);
        }
      });
    }

    return () => {
      commandListeners.forEach((listener) => {
        listener?.remove();
      });
    };
  }, [sbConnected, userId, viewIsReady]);

  return (
    <View style={{ alignItems: 'center', flex: 1, justifyContent: 'center' }}>
      {isError ? (
        <ErrorView onRetryButtonPress={_onRetryButtonPress} />
      ) : isFocused ? (
        <KitLoader />
      ) : null}
    </View>
  );
}
